import React from "react";
import { Outlet } from "react-router-dom";
import { Box } from "@mui/material";
import AppSidebar, {
  NavItemConfig,
  SubNavItemConfig,
} from "components/nav/AppSidebar";
import { AppNavbar, AppFooter } from "components/nav";
import useSidebarCollapsed from "hooks/useSidebarCollapsed";
import { SubNavProvider } from "context/SubNavContext";

const CONTENT_MAX_WIDTH = "1920px";

export interface AppShellProps {
  /**
   * Route prefix for this shell, without a trailing slash: `/my`, `/platform`,
   * `/admin`. Every nav route is built from it.
   */
  basePath: string;
  items: NavItemConfig[];
  subItems?: SubNavItemConfig[];
  /** Small-screen overlay state, owned by the route layout. */
  isSmallScreen: boolean;
  isSideBarSmallScreenOpen: boolean;
  handleCollapsed: () => void;
  /** The account menu for this shell (admin and platform differ). */
  ProfilePopoverComponent?: React.ComponentProps<
    typeof AppNavbar
  >["ProfilePopoverComponent"];
  /** Where the navbar's user links point. */
  /** Compliance line in the footer. Omit for no footer. */
  footerDisclosure?: string;
  /** Shell-level prompts and dialogs (not chrome), rendered after the footer. */
  prompts?: React.ReactNode;
  /** Extra navbar actions, before the Help button. */
  extraActions?: React.ReactNode;
  /** Extra navbar actions, in the left section after the mobile menu. */
  leadingActions?: React.ReactNode;
  /**
   * Padding around the content region.
   *
   * Not shared, because the shells genuinely differ: platform and admin pages
   * bring their own (mostly via the `Page` primitive), while several employee
   * pages, the dashboard included, have always relied on the layout for it.
   * Padding here unconditionally would double up on every page that already
   * pads itself.
   */
  contentPadding?: string;
}

/**
 * The application shell: rail, top bar, scrolling content region and footer.
 *
 * One component for `/admin`, `/platform` and `/my`, which differ only by
 * `basePath`, their nav items and their account menu. Everything structural is
 * shared, so a change to the chrome lands in all three at once.
 *
 * The rail is painted by the shell root rather than by itself: the package
 * `SideNav` is translucent, so it transmits whatever is behind it, and the
 * content pane's curve reveals the same surface. One token on the root is what
 * keeps the rail and that corner identical.
 */
const AppShell: React.FC<AppShellProps> = ({
  basePath,
  items,
  subItems,
  isSmallScreen,
  isSideBarSmallScreenOpen,
  handleCollapsed,
  ProfilePopoverComponent,
  footerDisclosure,
  prompts,
  extraActions,
  leadingActions,
  contentPadding,
}) => {
  const { collapsed, toggleCollapsed, navWidth } = useSidebarCollapsed();

  return (
    <SubNavProvider
      items={items}
      subItems={subItems}
      revealRail={
        isSmallScreen && !isSideBarSmallScreenOpen ? handleCollapsed : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          position: "relative",
          // The ground the rail sits on and transmits, and what the pane's curve
          // reveals. Opaque on purpose: the veil token would composite lighter
          // than this and the corner would read as a third surface.
          backgroundColor: "var(--color-bg-surface-canvas-solid)",
          minHeight: "100vh",
        }}
        // Layout only. Custom properties inherit, so a colour set here would
        // beat the package's :root block for the whole shell (R3).
        style={
          {
            "--layout-nav-width": navWidth,
            "--layout-content-width": CONTENT_MAX_WIDTH,
          } as React.CSSProperties
        }
      >
        {(!isSmallScreen || isSideBarSmallScreenOpen) && (
          <AppSidebar
            basePath={basePath}
            items={items}
            subItems={subItems}
            handleCollapsed={handleCollapsed}
            isSmallScreen={isSmallScreen}
            isSideBarSmallScreenOpen={isSideBarSmallScreenOpen}
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
          />
        )}

        {/* The content pane. Curves away from the rail, with the curve drawn by a
          hairline rather than fill contrast: in dark the pane and the rail's
          ground are close enough that a fill-only curve disappears. */}
        <Box
          sx={{
            maxWidth: isSmallScreen
              ? "100%"
              : "calc(100% - var(--layout-nav-width))",
            marginLeft: isSmallScreen ? 0 : "var(--layout-nav-width)",
            height: "100vh",
            overflow: "hidden",
            backgroundColor: "var(--color-bg-page)",
            borderTopLeftRadius: "var(--radius-lg)",
            borderBottomLeftRadius: "var(--radius-lg)",
            borderLeft: "var(--border-width-1) solid var(--color-border)",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            position: "relative",
          }}
        >
          <AppNavbar
            handleCollapsed={handleCollapsed}
            isSmallScreen={isSmallScreen}
            bg="var(--color-bg-page)"
            extraActions={extraActions}
            leadingActions={leadingActions}
            ProfilePopoverComponent={ProfilePopoverComponent}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {/* Bounded height, not just flex-grow: pages that scroll internally
                (TablePage -> DataTable) can only activate their own overflow-y
                against a parent with a real height. Without it every scroll
                bubbles up here and breaks the table's infinite-scroll
                listeners. */}
              <Box
                sx={{
                  maxWidth: "var(--layout-content-width)",
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  margin: "0 auto",
                  padding: contentPadding,
                }}
              >
                <Outlet />
              </Box>
            </Box>
          </Box>

          {footerDisclosure !== undefined && !isSideBarSmallScreenOpen && (
            <AppFooter
              disclosure={footerDisclosure}
              bg="var(--color-bg-page)"
            />
          )}
        </Box>

        {prompts}
      </Box>
    </SubNavProvider>
  );
};

export default AppShell;
